import type { DestroyOptions } from 'pixi.js';
import { Container, Graphics } from 'pixi.js';
import { DragController } from '../../utils/DragController';
import MathUtils from '../../../utils/MathUtils';

export interface HorizontalScrollBarConfig {
  readonly stage: Container;
  readonly width: number;
  readonly height: number;
  readonly backgroundColor: number;
  readonly knobColor: number;
  readonly cornerRadius: number;
  readonly onScrolled: () => void;
}

export class HorizontalScrollBar extends Container {
  private readonly config: HorizontalScrollBarConfig;

  private readonly background: Graphics;

  private readonly knob: Graphics;

  private knobRatio = 0.5;

  private readonly dragController: DragController;

  private scrollWidth: number;

  private readonly updateIntervalId: ReturnType<typeof setInterval>;

  constructor(config: HorizontalScrollBarConfig) {
    super();

    this.config = config;

    this.dragController = new DragController(config.stage);

    this.background = this.createBackground();

    this.knob = this.createKnob();

    this.scrollWidth = config.width;

    this.addChild(this.background);
    this.addChild(this.knob);

    this.updateIntervalId = setInterval((): void => {
      this.dragController?.update(1);
    }, 1000 / 60);
  }

  public destroy(options?: DestroyOptions): void {
    clearInterval(this.updateIntervalId);
    super.destroy(options);
    this.dragController.dispose();
  }

  private createBackground(): Graphics {
    const { width, height, backgroundColor, cornerRadius } = this.config;
    return this.drawRoundedRect(new Graphics(), width, height, backgroundColor, cornerRadius);
  }

  private createKnob(): Graphics {
    const { width, height, knobColor, cornerRadius, onScrolled } = this.config;
    const knob = this.drawRoundedRect(new Graphics(), width * this.knobRatio, height, knobColor, cornerRadius).props({
      parent: this,
    });

    knob.interactive = true;
    knob.on('pointerdown', () => {
      this.dragController.startDrag({
        container: knob,
        onUpdate: () => {
          const knobWidth = this.scrollWidth * this.knobRatio;
          knob.x = MathUtils.clamp(knob.x, 0, this.scrollWidth - knobWidth);
          knob.y = 0;
          onScrolled();
        },
      });
    });
    return knob;
  }

  private drawRoundedRect(
    graphics: Graphics,
    width: number,
    height: number,
    color: number,
    cornerRadius: number,
  ): Graphics {
    graphics.clear();
    graphics.roundRect(0, 0, width, height, cornerRadius).fill(color);
    return graphics;
  }

  public resize(width: number): void {
    if (this.destroyed) return;

    const { height, backgroundColor, knobColor, cornerRadius } = this.config;
    const knobPosition = this.getKnobPosition();
    const knobWidth = this.scrollWidth * this.knobRatio;

    this.scrollWidth = width;

    this.drawRoundedRect(this.background, width, height, backgroundColor, cornerRadius);
    this.drawRoundedRect(this.knob, knobWidth, height, knobColor, cornerRadius);

    this.knob.x = knobPosition * (this.scrollWidth - this.knob.width);
  }

  public getKnobPosition(): number {
    if (this.knob.destroyed || this.knob.x === 0) return 0;
    return this.knob.x / (this.scrollWidth - this.knob.width);
  }

  public resizeKnob(knobRatio: number): void {
    if (this.destroyed) return;
    this.knobRatio = knobRatio;
    this.resize(this.scrollWidth);
  }
}
